import { useCallback, useMemo, useRef, useState, type CSSProperties, type MouseEvent } from "react";
import { useDrag } from "react-dnd";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import moment from "moment-timezone";
import { omit } from "lodash";
import { Lock } from "lucide-react";
import Checkbox from "../../../../../../../../components/shared/inputs/Checkbox";
import BreakOvertimeIndicator from "../../../../../components/BreakOvertimeIndicator";
import SectionInfo from "../../../../../components/SectionInfo";
import ShiftTooltip from "../../../../../components/tooltip/ShiftTooltip";
import useApi from "../../../../../../../../utils/api";
import { useMenuContext } from "../../../../../hooks/context/useMenuContext";
import { useScheduleContext } from "../../../../../hooks/context/useScheduleContext";
import { useIsTemplate } from "../../../../../hooks/useIsTemplate";
import { useFilters } from "../../../../../../../../utils/hooks/useFilters";
import { useEventUser } from "../../../../../hooks/useEventUser";
import { useAddedPayCode } from "../../../../../../../TimeSheet/components/TimeSheetTable/hooks/useAddedPayCode";
import { useFormatDragAndDropData } from "../../../../../hooks/useFormatDragAndDropData";
import { usePermissions } from "../../../../../hooks/usePermissions";
import { useDefaultLevel } from "../../../../../hooks/useDefaultLevel";
import { useShiftPreferenceStyle } from "../../../../../hooks/styles/useShiftPreferenceStyle";
import { getScheduleUrl } from "../../../../../helpers/getScheduleUrl";
import { getErrorMessage } from "../../../../../../../../utils/helpers/errors";
import { calculateFormValues } from "../../../../../helpers/form/calculateFormValues";
import { getPrimaryActiveResource } from "../../../../../../../../utils/helpers/user";
import { getFormFormattedValues } from "../../../../../helpers/form/getFormFormattedValues";
import { getSectionDurations } from "../../../../../helpers/data/getSectionDurations";
import { renderSectionsDuration } from "../../../../../helpers/eventdisplay/renderSectionsDuration";
import OpenRequestIndicator from "../../../../../components/OpenRequestIndicator";
import { withLoadingToast } from "../../../../../helpers/withLoadingToast";
import { cn } from "../../../../../../../../lib/utils";

// Auto-scroll speed in px/sec at full intensity. Scales with elapsed time and how far past the
// edge threshold the cursor is, so the scroll eases in instead of jumping to a constant speed.
const MAX_SCROLL_SPEED = 600;
// Floor so speed doesn't feel imperceptibly slow right at the edge of the trigger zone.
const MIN_INTENSITY = 0.4;
const SCROLL_THRESHOLD = 50;
const MIN_DURATION_MINUTES = 15;
// Latest a shift may end within its day (23:45)
const MAX_END_MINUTES = 23 * 60 + 45;

type ResizeState = {
    direction: "left" | "right";
    element: HTMLDivElement;
    scrollContainer: Element | null;
    containerRect: DOMRect | null;
    initialX: number;
    initialWidth: number;
    initialLeft: number;
    initialScrollLeft: number;
    initialInlineWidth: string;
    initialInlineLeft: string;
    minutesPerPixel: number;
    dayStart: moment.Moment;
    // All times below are minutes from dayStart
    startMinutes: number;
    endMinutes: number;
    minStartMinutes: number;
    maxEndMinutes: number;
    newStartMinutes: number;
    newEndMinutes: number;
    // Last range reported through onPreview, so it only fires when the displayed minute changes
    previewStartMinutes: number;
    previewEndMinutes: number;
    onPreview: (startMinutes: number, endMinutes: number) => void;
    clientX: number;
    frame: number | null;
    lastFrameTime: number | null;
};

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

// The range the shift will be saved with if released now, rounded to the minute
function getResizedRange(state: ResizeState) {
    if (state.direction === "right") {
        return {
            start: state.startMinutes,
            end: clamp(Math.round(state.newEndMinutes), state.startMinutes + MIN_DURATION_MINUTES, state.maxEndMinutes),
        };
    }
    return {
        start: clamp(Math.round(state.newStartMinutes), state.minStartMinutes, state.endMinutes - MIN_DURATION_MINUTES),
        end: state.endMinutes,
    };
}

// Everything the resize needs per frame is precomputed at mousedown, and the block is styled
// directly on the DOM node (at most once per animation frame) instead of through React state,
// so dragging never waits on a re-render and follows the cursor 1:1.
function runResizeFrame(state: ResizeState, timestamp: number) {
    state.frame = null;
    const { scrollContainer, containerRect, clientX, minutesPerPixel, element } = state;

    let scrolled = false;
    if (scrollContainer && containerRect) {
        const last = state.lastFrameTime;
        const deltaSeconds = last == null ? (1 / 60) : Math.min(timestamp - last, 100) / 1000;
        const rightOvershoot = clientX - (containerRect.right - SCROLL_THRESHOLD);
        const leftOvershoot = containerRect.left + SCROLL_THRESHOLD - clientX;
        const overshoot = rightOvershoot > 0 ? rightOvershoot : leftOvershoot > 0 ? -leftOvershoot : 0;
        if (overshoot !== 0) {
            const intensity = MIN_INTENSITY + (1 - MIN_INTENSITY) * Math.min(1, Math.abs(overshoot) / SCROLL_THRESHOLD);
            const before = scrollContainer.scrollLeft;
            scrollContainer.scrollLeft += Math.sign(overshoot) * MAX_SCROLL_SPEED * intensity * deltaSeconds;
            scrolled = scrollContainer.scrollLeft !== before;
        }
    }
    state.lastFrameTime = scrolled ? timestamp : null;

    const scrollDelta = (scrollContainer?.scrollLeft ?? 0) - state.initialScrollLeft;
    const deltaMinutes = (clientX - state.initialX + scrollDelta) * minutesPerPixel;

    if (state.direction === "right") {
        const end = clamp(state.endMinutes + deltaMinutes, state.startMinutes + MIN_DURATION_MINUTES, state.maxEndMinutes);
        state.newEndMinutes = end;
        element.style.width = `${Math.max(20, state.initialWidth + (end - state.endMinutes) / minutesPerPixel)}px`;
    } else {
        const start = clamp(state.startMinutes + deltaMinutes, state.minStartMinutes, state.endMinutes - MIN_DURATION_MINUTES);
        state.newStartMinutes = start;
        const shift = (start - state.startMinutes) / minutesPerPixel;
        element.style.width = `${Math.max(20, state.initialWidth - shift)}px`;
        element.style.left = `${state.initialLeft + shift}px`;
    }

    const { start, end } = getResizedRange(state);
    if (start !== state.previewStartMinutes || end !== state.previewEndMinutes) {
        state.previewStartMinutes = start;
        state.previewEndMinutes = end;
        state.onPreview(start, end);
    }

    // Keep scrolling while the cursor sits in the edge zone, even without mouse movement
    if (scrolled) requestResizeFrame(state);
}

function requestResizeFrame(state: ResizeState) {
    if (state.frame == null) {
        state.frame = requestAnimationFrame((timestamp) => runResizeFrame(state, timestamp));
    }
}

type ResizeHandleProps = {
    side: "left" | "right";
    active: boolean;
    onMouseDown: (e: MouseEvent<HTMLDivElement>, direction: "left" | "right") => void;
};

// Edge grip: hinted while hovering the shift, highlighted on the handle itself and while resizing
function ResizeHandle({ side, active, onMouseDown }: ResizeHandleProps) {
    return (
        <div
            className={cn(
                "tw-group/handle tw-absolute tw-top-0 tw-bottom-0 tw-z-20 tw-w-2.5 tw-cursor-ew-resize",
                "tw-flex tw-items-center tw-justify-center tw-transition-colors hover:tw-bg-primary/15",
                side === "left" ? "tw-left-0 tw-rounded-l-md" : "tw-right-0 tw-rounded-r-md",
                active && "tw-bg-primary/15",
            )}
            onMouseDown={(e) => onMouseDown(e, side)}
        >
            <span
                className={cn(
                    "tw-h-4 tw-w-1 tw-rounded-full tw-bg-primary/40 tw-opacity-0 tw-transition-all",
                    "group-hover:tw-opacity-100 group-hover/handle:tw-h-6 group-hover/handle:tw-bg-primary",
                    active && "tw-h-6 tw-bg-primary tw-opacity-100",
                )}
            />
        </div>
    );
}

function Shift({ event, className, style, cellWidth }: any) {
    const { t } = useTranslation();
    const localRef = useRef<HTMLDivElement>(null);
    const [overrideStyle, setOverrideStyle] = useState<CSSProperties>({});
    const [resizingSide, setResizingSide] = useState<"left" | "right" | null>(null);
    // Live start/end shown while resizing, kept until the update comes back
    const [resizePreview, setResizePreview] = useState<{ startDate: moment.Moment; endDate: moment.Moment } | null>(null);
    const { authPut } = useApi();
    const { openMenu } = useMenuContext();
    const { dispatch, selectedEvents, events, type, templates } = useScheduleContext();
    const isTemplate = useIsTemplate();
    const [{ template, day }] = useFilters();
    const user = useEventUser(event?.resourceId);
    const defaultPayCode = useAddedPayCode(user);
    const formatDragAndDropData = useFormatDragAndDropData({ resource: user });
    const { canCreate, canEdit } = usePermissions();

    const resizeData = useRef<ResizeState | null>(null);

    const [{ isDragging }, drag] = useDrag(() => ({
        type: "EVENT",
        item: { ...event, user: event?.user?.id || event?.user },
        collect: (monitor) => ({ isDragging: !!monitor.isDragging() }),
        canDrag: (canCreate || canEdit) && !event?.locked,
        options: {
            dropEffect: "copy"
        }
    }), [event, canCreate, canEdit]);

    const combinedRef = (node: HTMLDivElement | null) => {
        localRef.current = node;
        drag(node);
    };

    const editEventMutation = useMutation({
        ...withLoadingToast(t("saving")),
        mutationFn: (data: any) => {
            const templateId = template?.id || templates?.[0]?.id;
            const url = getScheduleUrl(type, templateId, data.id);
            return authPut(url, { data: omit(data, ["id"]) });
        },
        onSuccess: (response: any) => {
            const userTimezone = moment.tz.guess();
            toast.success(t("event-updated-successfully"));

            dispatch({
                type: "update-event",
                payload: {
                    ...response,
                    resourceId: response?.user?.id || response?.user,
                    startDate: moment.parseZone(response.startDate).tz(userTimezone, true),
                    endDate: moment.parseZone(response.endDate).tz(userTimezone, true)
                },
            });
            setOverrideStyle({});
            setResizePreview(null);
        },
        onError: (err) => {
            getErrorMessage(err, t);
            setOverrideStyle({});
            setResizePreview(null);
        }
    });

    const commitResize = useCallback((state: ResizeState) => {
        const { direction, element, dayStart, minutesPerPixel } = state;
        const originalStartDate = moment.parseZone(event.startDate).local();
        const originalEndDate = moment.parseZone(event.endDate).local();
        const { start, end } = getResizedRange(state);

        let finalStartDate = originalStartDate;
        let finalEndDate = originalEndDate;
        let nextStyle: CSSProperties | null = null;

        if (direction === "right" && state.newEndMinutes !== state.endMinutes) {
            finalEndDate = dayStart.clone().add(end, "minutes");
            nextStyle = { width: `${Math.max(20, state.initialWidth + (end - state.endMinutes) / minutesPerPixel)}px` };
        } else if (direction === "left" && state.newStartMinutes !== state.startMinutes) {
            finalStartDate = dayStart.clone().add(start, "minutes");
            const shift = (start - state.startMinutes) / minutesPerPixel;
            nextStyle = {
                width: `${Math.max(20, state.initialWidth - shift)}px`,
                left: `${state.initialLeft + shift}px`,
            };
        }

        if (!nextStyle || (finalStartDate.isSame(originalStartDate) && finalEndDate.isSame(originalEndDate))) {
            element.style.width = state.initialInlineWidth;
            element.style.left = state.initialInlineLeft;
            setResizePreview(null);
            return;
        }

        // Settle on the final position until the update comes back
        Object.assign(element.style, nextStyle);
        setOverrideStyle(nextStyle);
        setResizePreview({ startDate: finalStartDate, endDate: finalEndDate });

        const data = formatDragAndDropData(event, finalStartDate, finalEndDate);
        const firstSection = data.sections?.[0];
        const values = calculateFormValues(
            {
                ...data,
                job: firstSection?.job,
                location: firstSection?.location,
                skills: firstSection?.skills,
                licenses: firstSection?.licenses,
                certifications: firstSection?.certifications
            },
            {
                user,
                isCreating: false,
                sections: data.sections
            },
            {
                payCode: defaultPayCode,
                location: getPrimaryActiveResource(user?.locations || []),
                job: getPrimaryActiveResource(user?.jobs || []),
                type: "regular",
                start: moment(data.startDate).toISOString(true),
                end: moment(data.endDate).toISOString(true)
            }
        );

        editEventMutation.mutate({
            ...getFormFormattedValues(values),
            id: data?.id,
            date: isTemplate ? day : data?.date
        });
    }, [event, formatDragAndDropData, user, defaultPayCode, editEventMutation, isTemplate, day]);

    const handleMouseDown = useCallback(
        (e: MouseEvent<HTMLDivElement>, direction: "left" | "right") => {
            e.preventDefault();
            e.stopPropagation();
            const element = localRef.current;
            if (!element || resizeData.current) return;

            const scrollContainer = element.closest(".schedule-grid-container");
            const originalStartDate = moment.parseZone(event.startDate).local();
            const originalEndDate = moment.parseZone(event.endDate).local();
            const dayStart = originalStartDate.clone().startOf("day");
            const toMinutes = (date: moment.Moment) => date.diff(dayStart, "minutes", true);
            const startMinutes = toMinutes(originalStartDate);
            const endMinutes = toMinutes(originalEndDate);

            // Unavailable blocks of this resource stop the resize at their edge
            let minStartMinutes = 0;
            let maxEndMinutes = MAX_END_MINUTES;
            events?.forEach((item: any) => {
                if (item.type !== "unavailable" || item.id === event.id || item.resourceId !== event.resourceId) return;
                const itemStart = toMinutes(moment.parseZone(item.startDate).local());
                const itemEnd = toMinutes(moment.parseZone(item.endDate).local());
                if (itemStart >= endMinutes) maxEndMinutes = Math.min(maxEndMinutes, itemStart);
                if (itemEnd <= startMinutes) minStartMinutes = Math.max(minStartMinutes, itemEnd);
            });

            const state: ResizeState = {
                direction,
                element,
                scrollContainer,
                containerRect: scrollContainer ? scrollContainer.getBoundingClientRect() : null,
                initialX: e.clientX,
                initialWidth: element.getBoundingClientRect().width,
                initialLeft: parseFloat(getComputedStyle(element).left) || element.offsetLeft,
                initialScrollLeft: scrollContainer ? scrollContainer.scrollLeft : 0,
                initialInlineWidth: element.style.width,
                initialInlineLeft: element.style.left,
                minutesPerPixel: 60 / cellWidth,
                dayStart,
                startMinutes,
                endMinutes,
                minStartMinutes,
                maxEndMinutes,
                newStartMinutes: startMinutes,
                newEndMinutes: endMinutes,
                previewStartMinutes: startMinutes,
                previewEndMinutes: endMinutes,
                onPreview: (start, end) => setResizePreview({
                    startDate: dayStart.clone().add(start, "minutes"),
                    endDate: dayStart.clone().add(end, "minutes"),
                }),
                clientX: e.clientX,
                frame: null,
                lastFrameTime: null,
            };
            resizeData.current = state;

            // Keep the resize cursor and avoid text selection while the pointer leaves the handle
            const { cursor, userSelect } = document.body.style;
            document.body.style.cursor = "ew-resize";
            document.body.style.userSelect = "none";
            setResizingSide(direction);

            const onMouseMove = (moveEvent: globalThis.MouseEvent) => {
                state.clientX = moveEvent.clientX;
                requestResizeFrame(state);
            };
            const onMouseUp = () => {
                window.removeEventListener("mousemove", onMouseMove);
                if (state.frame != null) cancelAnimationFrame(state.frame);
                state.frame = null;
                document.body.style.cursor = cursor;
                document.body.style.userSelect = userSelect;
                resizeData.current = null;
                setResizingSide(null);
                commitResize(state);
            };
            window.addEventListener("mousemove", onMouseMove);
            window.addEventListener("mouseup", onMouseUp, { once: true });
        },
        [event.startDate, event.endDate, event.id, event.resourceId, events, cellWidth, commitResize],
    );

    const onMouseLeave = useCallback((e: any) => {
        const copyActive = e.getModifierState("Control") || e.getModifierState("Meta");
        dispatch({ type: "drag-and-drop-copy", payload: copyActive })
    }, [dispatch]);

    const handleDoubleClick = useCallback((e: any) => {
        e.stopPropagation();
        dispatch({
            type: "selected-cell-or-event",
            payload: {
                isCreating: false,
                ...event,
                isTemplate,
                user
            }
        })
    }, [dispatch, user, event, isTemplate]);

    const onContextMenu = useCallback((e: any) => {
        e.preventDefault();
        e.stopPropagation();
        if (type === "my-profile") return;
        openMenu({
            id: "event-menu",
            x: e.clientX,
            y: e.clientY + 10,
            selected: { ...event, user: event?.resource || event?.user }
        });
    }, [openMenu, event, type]);

    const selectedElement = useMemo(() => {
        return !!selectedEvents?.find((item: any) => item.id === event.id);
    }, [selectedEvents, event.id]);

    const onEventChange = useCallback(() => {
        dispatch({ type: "select-event", payload: { event } });
    }, [dispatch, event]);

    const { overtimeDuration, breakDuration } = getSectionDurations(event?.sections);

    const defaultLevel = useDefaultLevel();

    const preferenceStyle = useShiftPreferenceStyle(event);

    const displayStartDate = resizePreview?.startDate ?? moment(event.startDate);
    const displayEndDate = resizePreview?.endDate ?? moment(event.endDate);

    return (
        <ShiftTooltip data={event} isOpen={resizingSide ? false : undefined}>
            <div
                ref={combinedRef}
                className={cn(
                    "work-block tw-group tw-bg-indigo-100 tw-rounded-md tw-px-3 tw-py-2 tw-cursor-pointer tw-border-l-2 tw-border-indigo-300",
                    "tw-flex tw-items-center tw-absolute border-box tw-z-10 tw-overflow-hidden",
                    event.status === "approved" && "tw-bg-green-100 tw-border-l-green-400",
                    event?.locked && "tw-bg-violet-100 tw-border-l-violet-400",
                    event?.status === "submitted" && "tw-bg-rose-100 tw-border-l-rose-400",
                    isTemplate && "tw-bg-indigo-100 tw-border-l-primary/30",
                    className || "",
                )}
                style={{
                    ...style,
                    ...overrideStyle,
                    ...preferenceStyle,
                    opacity: isDragging ? 0.5 : 1
                }}
                onDoubleClick={type !== "my-profile" ? handleDoubleClick : undefined}
                onMouseLeave={onMouseLeave}
                onContextMenu={onContextMenu}
            >
                {(canEdit && !event?.locked && type !== "my-profile") && (
                    <ResizeHandle side="left" active={resizingSide === "left"} onMouseDown={handleMouseDown} />
                )}
                <div className="tw-w-full tw-flex tw-justify-between tw-items-center tw-gap-3">
                    <div className="tw-flex tw-flex-col tw-min-w-0 tw-flex-1">
                        <div className="tw-flex tw-items-center tw-justify-between tw-w-full">
                            <div className="tw-font-bold tw-text-sm tw-flex tw-items-center tw-gap-2 tw-truncate">
                                {event?.locked && (<Lock size={16} strokeWidth={3} />)}
                                <OpenRequestIndicator event={event} />
                                {displayStartDate.format("h:mma")}
                                {" - "}
                                {displayEndDate.format("h:mma")}
                                <span className="tw-font-normal tw-text-xs tw-text-neutral-600">
                                    {"("}
                                    {renderSectionsDuration(
                                        displayEndDate.diff(displayStartDate, "seconds"),
                                        event.sections
                                    )}
                                    {")"}
                                </span>
                            </div>

                            <BreakOvertimeIndicator
                                breakDuration={breakDuration}
                                overtimeDuration={overtimeDuration}
                                size={17}
                            />
                        </div>

                        <SectionInfo
                            section={event?.sections?.[0]}
                            payCode={defaultPayCode?.code}
                            defaultLevel={defaultLevel}
                            className="tw-pt-0.5"
                        />
                    </div>

                    <div
                        className="tw-shrink-0"
                        onClick={(e) => e.stopPropagation()}
                        onDoubleClick={(e) => e.stopPropagation()}
                    >
                        <Checkbox
                            className="tw-bg-white"
                            checked={selectedElement}
                            onCheckedChange={onEventChange}
                        />
                    </div>
                </div>
                {(canEdit && !event?.locked && type !== "my-profile") && (
                    <ResizeHandle side="right" active={resizingSide === "right"} onMouseDown={handleMouseDown} />
                )}
            </div>
        </ShiftTooltip>
    );
}

export default Shift;
